import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, FileText, ShieldCheck, Check, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { CountyLeaderboard } from "../components/CountyLeaderboard";
import { ProposalTracker } from "../components/ProposalTracker";
import { FiledSites } from "../components/FiledSites";

const input =
  "mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-ochre focus:outline-none";
const label = "mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400";

const EMPTY = {
  full_name: "", email: "", mobile: "", organisation: "",
  county: "", opportunity_category: "", description: "", authority: "",
  consent_contact: false, appoint_own_professional: false, consent_updates: false, consent_marketing: false,
  film_interest: false, film_stage: "",
  declare_accurate: false, accept_terms: false,
};

const STEPS = ["Who you are", "The opportunity", "Consent and terms"];

const REQUIRED = {
  0: ["full_name", "email", "mobile"],
  1: ["county", "opportunity_category", "description", "authority"],
};

const Field = ({ name, text, required, placeholder, span = "", form, set, area, type = "text" }) => (
  <label className={`block ${span}`}>
    <span className={label}>
      {text}
      {required ? <span className="text-maasai"> *</span> : null}
    </span>
    {area ? (
      <textarea name={name} rows={5} value={form[name]} onChange={set} placeholder={placeholder}
        data-testid={`apply-${name.replace(/_/g, "-")}`} className={input} />
    ) : (
      <input name={name} type={type} value={form[name]} onChange={set} placeholder={placeholder}
        data-testid={`apply-${name.replace(/_/g, "-")}`} className={input} />
    )}
  </label>
);

const Select = ({ name, text, required, options, form, set, span = "" }) => (
  <label className={`block ${span}`}>
    <span className={label}>
      {text}
      {required ? <span className="text-maasai"> *</span> : null}
    </span>
    <select name={name} value={form[name]} onChange={set} data-testid={`apply-${name.replace(/_/g, "-")}`}
      className={`${input} bg-black`}>
      <option value="">Choose one</option>
      {(options || []).map((o) => {
        const [value, text2] = Array.isArray(o) ? o : [o, o];
        return <option key={value} value={value}>{text2}</option>;
      })}
    </select>
  </label>
);

const Box = ({ name, children, form, toggle }) => (
  <label className="flex cursor-pointer items-start gap-3 border border-white/10 bg-[#0b0b0b] p-4 transition-colors hover:border-ochre/50">
    <input type="checkbox" name={name} checked={form[name]} onChange={toggle}
      data-testid={`apply-${name.replace(/_/g, "-")}`} className="mt-0.5 h-4 w-4 shrink-0 accent-ochre" />
    <span className="text-sm leading-relaxed text-zinc-300">{children}</span>
  </label>
);

/** A preliminary expression of interest: contact details, the opportunity, unticked consent, then the terms. */
export default function Apply() {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const token = params.get("draft") || "";
  const formTop = useRef(null);

  // Each step is a page to read from the top, not a form that jumps you into the middle of itself.
  const goToStep = (i) => {
    setStep(i);
    requestAnimationFrame(() => {
      const top = formTop.current?.getBoundingClientRect().top ?? 0;
      window.scrollTo({ top: Math.max(0, top + window.scrollY - 90), behavior: "smooth" });
    });
  };

  useSeo({
    title: "Tell Us About A Football Opportunity | KickStartKenyaFootball",
    description:
      "Tell us about a football site, a real build cost, a land price or a grassroots project in your county, and we will add it to the research with a source.",
  });

  useEffect(() => {
    api.get("/apply/meta").then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, []);

  // A saved proposal reopens exactly where it was left, consents excluded: those are given, never restored.
  useEffect(() => {
    if (!token) return;
    api
      .get(`/apply/draft/${token}`)
      .then((r) => {
        setForm((f) => ({ ...f, ...r.data.form }));
        setStep(r.data.step || 0);
        setDraft({ token, saved_at: r.data.saved_at, expires_at: r.data.expires_at });
        toast.success("Picked up where you left off. Nothing has been sent yet.");
      })
      .catch(() => toast.error("That saved proposal has expired. Start a new one — it only takes a few minutes."));
  }, [token]);

  const saveDraft = async () => {
    if (!String(form.email || "").trim()) {
      return toast.error("Add your email first — that is where the link to come back is sent.");
    }
    try {
      setSaving(true);
      const { data } = await api.post("/apply/draft", { email: form.email, form, step });
      setDraft({ token: data.token, saved_at: new Date().toISOString(), expires_at: data.expires_at });
      const next = new URLSearchParams(params);
      next.set("draft", data.token);
      setParams(next, { replace: true });
      toast.success("Saved. The link to come back is on its way to your email.");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const discardDraft = async () => {
    if (!draft?.token) return;
    try {
      await api.delete(`/apply/draft/${draft.token}`);
      setDraft(null);
      setForm(EMPTY);
      const next = new URLSearchParams(params);
      next.delete("draft");
      setParams(next, { replace: true });
      goToStep(0);
      toast.success("Draft deleted. Nothing of it is kept.");
    } catch {
      toast.error("We could not delete that draft. Try again.");
    }
  };

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const toggle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.checked }));

  const missing = (i) => (REQUIRED[i] || []).filter((k) => !String(form[k] ?? "").trim());
  const stepReady = (i) => missing(i).length === 0;
  const allAnswered = [0, 1].every(stepReady);

  const next = () => {
    if (!stepReady(step)) return toast.error("Answer every question marked with a star before you continue.");
    goToStep(Math.min(step + 1, 2));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!allAnswered) return toast.error("Some questions are still unanswered.");
    try {
      setBusy(true);
      const { data } = await api.post("/apply", form);
      setDone(data);
      if (draft?.token) {
        await api.delete(`/apply/draft/${draft.token}`).catch(() => {});
        setDraft(null);
        const next = new URLSearchParams(params);
        next.delete("draft");
        setParams(next, { replace: true });
      }
      toast.success(`Logged — reference ${data.reference}`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const pdfHref = done
    ? (done.pdf_url.startsWith("http") ? done.pdf_url : `${process.env.REACT_APP_BACKEND_URL}${done.pdf_url}`)
    : "";

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="apply-page">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Open to everyone, in all 47 counties</p>
          <h1 className="display-1 mt-5 max-w-4xl">
            {meta?.heading || "Tell Us About A Football Opportunity In Your County"}
          </h1>
          <p className="body-copy mt-6 max-w-3xl">{meta?.intro}</p>
        </Reveal>

        {meta?.what_this_is?.length ? (
          <div className="mt-12 grid gap-px bg-zinc-800 md:grid-cols-3" data-testid="apply-what-this-is">
            {meta.what_this_is.map(([title, body], i) => (
              <div key={title} className="bg-[#0b0b0b] p-6" data-testid={`apply-what-${i}`}>
                <p className="text-sm font-semibold text-ochre">{title}</p>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{body}</p>
              </div>
            ))}
          </div>
        ) : null}

        {done ? (
          <div className="mt-16 max-w-3xl border-l-2 border-ochre bg-[#0b0b0b] p-8" data-testid="apply-done">
            <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-ochre">Reference {done.reference}</p>
            <h2 className="mt-4 text-base font-semibold text-white md:text-lg">Logged — and that is all you need to do</h2>
            <p className="mt-4 text-sm leading-relaxed text-zinc-300">
              It is on file and a person will read it.{" "}
              <span className="text-white">Please do not send documents</span> — no ID, KRA PIN, bank details or
              title papers. We do not want them and will never ask for them by email.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={pdfHref} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2"
                data-testid="apply-pdf-link">
                <FileText size={14} /> Download your submission record
              </a>
              <button onClick={() => { setDone(null); setForm(EMPTY); goToStep(0); }} className="btn-ghost"
                data-testid="apply-again">
                Tell us about another
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-16 max-w-4xl" data-testid="apply-form">
            <div ref={formTop} className="no-scrollbar flex gap-1 overflow-x-auto border-b border-zinc-900" data-testid="apply-steps">
              {STEPS.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => goToStep(i)}
                  disabled={i === 2 && !allAnswered}
                  data-testid={`apply-step-${i}`}
                  className={`shrink-0 px-4 py-3 text-[0.62rem] font-bold uppercase tracking-[0.14em] transition-colors disabled:opacity-40 ${
                    step === i ? "bg-maasai text-white" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {i < 2 && stepReady(i) ? <Check size={11} className="mr-1.5 inline text-ochre" /> : null}
                  {i + 1}. {s}
                </button>
              ))}
            </div>

            {step === 0 ? (
              <div className="mt-10" data-testid="apply-section-applicant">
                <h2 className="text-base font-semibold text-white md:text-lg">Who you are</h2>
                <p className="mt-3 max-w-3xl text-sm text-zinc-500">
                  Four lines, so we can reply. No ID number, no KRA PIN, no bank details, nothing to upload.
                </p>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <Field name="full_name" text="Name" required placeholder="What we should call you" form={form} set={set} />
                  <Field name="email" text="Email" required type="email" placeholder="Where your record goes" form={form} set={set} />
                  <Field name="mobile" text="Telephone" required placeholder="+254…" form={form} set={set} />
                  <Field name="organisation" text="Organisation, if there is one" placeholder="Club, school, group or company" form={form} set={set} />
                </div>
              </div>
            ) : null}

            {step === 1 ? (
              <div className="mt-10" data-testid="apply-section-opportunity">
                <h2 className="text-base font-semibold text-white md:text-lg">The opportunity</h2>
                <p className="mt-3 max-w-3xl text-sm text-zinc-500">
                  Tell it plainly, in your own words.
                </p>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <Field name="county" text="County" required placeholder="e.g. Kakamega" form={form} set={set} />
                  <Select name="opportunity_category" text="Broad category" required
                    options={meta?.opportunity_categories} form={form} set={set} />
                  <Select name="authority" text="Your standing to discuss it" required span="sm:col-span-2"
                    options={meta?.authority_options} form={form} set={set} />
                  <Field name="description" text="Describe it briefly" required area span="sm:col-span-2"
                    placeholder="Where it is, what is there now, who uses it and what you think could happen"
                    form={form} set={set} />
                </div>
                <p className="mono mt-6 max-w-3xl border-l-2 border-zinc-700 bg-[#0b0b0b] p-5 text-[0.58rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400"
                  data-testid="apply-no-documents-note">
                  {meta?.no_documents_note}
                </p>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="mt-10" data-testid="apply-section-review">
                <h2 className="text-base font-semibold text-white md:text-lg">Review and send</h2>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-400">
                  Nothing is ticked for you and you can withdraw any of it at any time.
                </p>

                <div className="mt-8 grid gap-4" data-testid="apply-consents">
                  {(meta?.consents || []).map((c) => (
                    <Box key={c.id} name={c.id} form={form} toggle={toggle}>
                      <span className="text-white">{c.label}</span>
                      {c.required ? <span className="text-maasai"> *</span> : null}
                      <span className="mt-1 block text-xs text-zinc-500">{c.note}</span>
                    </Box>
                  ))}
                </div>

                {meta?.film ? (
                  <div className="mt-14 border-l-2 border-ochre bg-[#0b0b0b] p-6 md:p-7" data-testid="apply-film">
                    <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-ochre">{meta.film.eyebrow}</p>
                    <h2 className="mt-3 text-base font-semibold text-white md:text-lg">{meta.film.heading}</h2>
                    <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-400">{meta.film.body}</p>
                    <div className="mt-6 grid gap-4">
                      <Box name="film_interest" form={form} toggle={toggle}>
                        {meta.film.label}
                      </Box>
                      {form.film_interest ? (
                        <Select name="film_stage" text={meta.film.stage_label} options={meta.film.stages}
                          form={form} set={set} />
                      ) : null}
                    </div>
                    <ul className="mt-6 space-y-2 border-t border-white/10 pt-5">
                      {meta.film.terms.map((t, i) => (
                        <li key={i} className="text-xs leading-relaxed text-zinc-500" data-testid={`apply-film-term-${i}`}>
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {meta?.referrals?.length ? (
                  <div className="mt-14" data-testid="apply-referrals">
                    <h2 className="text-base font-semibold text-white md:text-lg">
                      What we do not do, and who it goes to instead
                    </h2>
                    <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-400">
                      KickStartKenyaFootball performs no work that requires a Kenyan licence, registration or
                      practising certificate. These professionals are independent of us — we do not appoint,
                      authorise, accredit, approve or licence anyone, we do not send them your submission, and
                      we are paid nothing by them or by you. You contact and appoint them yourself.
                    </p>
                    <div className="mt-6 overflow-x-auto">
                      <table className="w-full min-w-[620px] text-left" data-testid="apply-referrals-table">
                        <thead>
                          <tr className="border-b border-white/15 bg-[#0b0b0b]">
                            {["The question", "Who it goes to", "Their regulator"].map((h) => (
                              <th key={h} className="mono px-4 py-3 text-[0.52rem] uppercase tracking-[0.16em] text-zinc-400">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {meta.referrals.map((r, i) => (
                            <tr key={r.question} className="border-b border-white/5" data-testid={`apply-referral-${i}`}>
                              <td className="px-4 py-3 text-sm text-white">{r.question}</td>
                              <td className="px-4 py-3 text-sm text-ochre">{r.who}</td>
                              <td className="px-4 py-3 text-xs text-zinc-400">{r.regulator}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                {meta?.terms?.length ? (
                  <div className="mt-14" data-testid="apply-terms">
                    <h2 className="text-base font-semibold text-white md:text-lg">Terms</h2>
                    <div className="mt-6 space-y-px bg-zinc-800">
                      {meta.terms.map((t, i) => (
                        <div key={t.title} className="bg-[#0b0b0b] p-5 md:p-6" data-testid={`apply-term-${i}`}>
                          <p className="text-sm font-semibold text-white">{t.title}</p>
                          <p className="mt-2 text-sm leading-relaxed text-zinc-400">{t.body}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="mt-10 grid gap-4">
                  <Box name="declare_accurate" form={form} toggle={toggle}>
                    Everything I have written is true and accurate to the best of my knowledge.
                  </Box>
                  <Box name="accept_terms" form={form} toggle={toggle}>
                    I accept the short terms above.
                  </Box>
                </div>
              </div>
            ) : null}

            <div className="mt-10 flex flex-wrap items-center gap-3">
              {step > 0 ? (
                <button type="button" onClick={() => goToStep(step - 1)} className="btn-ghost inline-flex items-center gap-2" data-testid="apply-back">
                  <ArrowLeft size={14} /> Back
                </button>
              ) : null}
              {step < 2 ? (
                <button type="button" onClick={next} className="btn-primary inline-flex items-center gap-2" data-testid="apply-next">
                  Continue <ArrowRight size={14} />
                </button>
              ) : (
                <button type="submit"
                  disabled={busy || !allAnswered || !form.declare_accurate || !form.accept_terms || !form.consent_contact || !form.appoint_own_professional}
                  data-testid="apply-submit" className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
                  {busy ? <><Loader2 size={14} className="animate-spin" /> Sending</> : <>Send it in <ArrowRight size={14} /></>}
                </button>
              )}
              {step < 2 && !stepReady(step) ? (
                <span className="mono text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500" data-testid="apply-step-hint">
                  {missing(step).length} question{missing(step).length === 1 ? "" : "s"} still to answer
                </span>
              ) : null}
              <button type="button" onClick={saveDraft} disabled={saving}
                className="btn-ghost ml-auto inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="apply-save-draft">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save and finish later
              </button>
            </div>

            {draft ? (
              <div className="mt-6 flex flex-wrap items-center gap-4 border-l-2 border-ochre bg-[#0b0b0b] p-5"
                data-testid="apply-draft-note">
                <p className="max-w-2xl text-xs leading-relaxed text-zinc-400">
                  <span className="text-white">Saved, not sent.</span> The link to reopen this page with your
                  answers in it is in your email and works for 30 days. Nobody reads a draft, and the consents
                  are still yours to tick when you are ready to send.
                </p>
                <button type="button" onClick={discardDraft}
                  className="mono ml-auto inline-flex items-center gap-2 text-[0.58rem] uppercase tracking-[0.14em] text-maasai hover:text-white"
                  data-testid="apply-discard-draft">
                  <Trash2 size={12} /> Delete the saved draft
                </button>
              </div>
            ) : null}

            <p className="mono mt-8 flex max-w-3xl items-start gap-3 text-[0.58rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500"
              data-testid="apply-privacy">
              <ShieldCheck size={15} className="mt-0.5 shrink-0 text-ochre" />
              {meta?.privacy}
            </p>
          </form>
        )}

        <ProposalTracker />
        <CountyLeaderboard limit={12} refreshKey={done ? done.reference : ""} />
        <FiledSites limit={10} />

        <div className="mt-20 flex flex-wrap gap-3" data-testid="apply-related">
          <Link to="/facility-planner" className="btn-ghost">See an indicative scenario for your county</Link>
          <Link to="/modular-stadiums" className="btn-ghost">How modular stadiums are costed</Link>
          <Link to="/about" className="btn-ghost">How this is governed</Link>
        </div>
      </div>
    </div>
  );
}
